import { useEffect, useState } from 'react';
import { ControlPosition, MapControl, useMap } from '@vis.gl/react-google-maps';

import colors from '@atom/styles/colors';

import { DEFAULT_MAP_HEADING, DEFAULT_MAP_TILT } from '../mapConfig';

import classes from './toolbar.module.css';

const RESET_ANIMATION_MS = 300;

const SUB_TICKS_BETWEEN_CARDINALS = 3;
const TICK_STEP_DEGREES = 90 / (SUB_TICKS_BETWEEN_CARDINALS + 1);

const TICKS = Array.from(
  { length: 360 / TICK_STEP_DEGREES },
  (_, index) => index * TICK_STEP_DEGREES,
);

const CARDINAL_TICK = { inner: 13, opacity: 1, width: 1.5 };
const MINOR_TICK = { inner: 12.5, opacity: 0.35, width: 1 };

const easeOutCubic = (progress: number) => 1 - (1 - progress) ** 3;

const turnToNorth = (heading: number) =>
  ((((DEFAULT_MAP_HEADING - heading) % 360) + 540) % 360) - 180;

const tickPoint = (degrees: number, radius: number) => ({
  x: 18 + radius * Math.sin((degrees * Math.PI) / 180),
  y: 18 - radius * Math.cos((degrees * Math.PI) / 180),
});

const MapCompass = () => {
  const map = useMap();

  const [heading, setHeading] = useState(DEFAULT_MAP_HEADING);

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const syncHeading = () =>
      setHeading(map.getHeading() ?? DEFAULT_MAP_HEADING);

    syncHeading();

    const listener = map.addListener('heading_changed', syncHeading);

    return () => listener.remove();
  }, [map]);

  const handleReset = () => {
    if (!map) {
      return;
    }

    const startHeading = map.getHeading() ?? DEFAULT_MAP_HEADING;
    const startTilt = map.getTilt() ?? DEFAULT_MAP_TILT;
    const turn = turnToNorth(startHeading);

    requestAnimationFrame(startTime => {
      const step = (now: number) => {
        const progress = Math.min((now - startTime) / RESET_ANIMATION_MS, 1);
        const eased = easeOutCubic(progress);

        map.moveCamera({
          heading: startHeading + turn * eased,
          tilt: startTilt + (DEFAULT_MAP_TILT - startTilt) * eased,
        });

        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };

      step(startTime);
    });
  };

  const renderTick = (degrees: number) => {
    const { inner, opacity, width } =
      degrees % 90 === 0 ? CARDINAL_TICK : MINOR_TICK;

    const start = tickPoint(degrees, 15);
    const end = tickPoint(degrees, inner);

    return (
      <line
        key={degrees}
        x1={start.x}
        y1={start.y}
        x2={end.x}
        y2={end.y}
        stroke={colors.neutral.white}
        strokeOpacity={opacity}
        strokeWidth={width}
        strokeLinecap="round"
      />
    );
  };

  return (
    <MapControl position={ControlPosition.INLINE_END_BLOCK_END}>
      <div
        className={classes.compassControl}
        onClick={handleReset}
        role="button"
        data-cy="atomMapCompassButton"
      >
        <svg className={classes.compassFace} viewBox="0 0 36 36">
          <circle cx={18} cy={18} r={18} fill={colors.neutral.dark} />
          {TICKS.map(renderTick)}
          <g transform={`rotate(${-heading} 18 18)`}>
            <path d="M18 7L20.6 18 15.4 18z" fill={colors.brand.red} />
            <path d="M18 29L15.4 18 20.6 18z" fill={colors.neutral.white} />
          </g>
        </svg>
      </div>
    </MapControl>
  );
};

export default MapCompass;
